import React, { useEffect, useState } from 'react';
import { Table, Modal, Breadcrumb, Button, Card, Row, Col, Tag, Input } from 'antd';
import { HomeOutlined, EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Link } from 'react-router-dom';
import axios from 'axios';
import useUserProfile from '../../useUserProfile';
import { Tooltip } from 'antd'; // Import Tooltip from Ant Design
import { useTranslation } from 'react-i18next';
import AppHeaderHomePageTemplate from '../Organism/AppHeaderHomePageTemplate';
import { title } from 'process';
import { assign } from '@ant-design/plots/es/core/utils';
import { render } from '@testing-library/react';

interface Nomination {
  nominationId: string;
  awardName: string;
  nominatorId: string;
  nominatorName: string;
  NomineeId: string[];
  nomineeNames: string[];
  type: string;
  assignedBy: string;
  assignedByName: string;
  status: string;
  approvalId: string;
}

interface PeerComment {
  peerId: string;
  peerName: string;
  peerComment: string;
}

interface NominationDetails {
  nominationId: string;
  nominatorName: string;
  nominatorId: string;
  nomineeNames: string[];
  nomineeIds: string[];
  TeamName: string;
  valueName: string;
  citation: number;
  contributionTowards: string;
  businessOutcome: string;
  managerComments: string;
  firstLevelApproverComments: string;
  secondLevelApproverComments: string;
  managerNames: string;
  firstLevelApproverNames: string;
  secondLevelApproverNames: string;
  peerComments: PeerComment[];
  awardName: string;
  nominationDate: string;
}

const PeersCommentsComponent: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [selectedNomination, setSelectedNomination] = useState<NominationDetails | null>(null);
  const [peerComment, setPeerComment] = useState<string>('');
  const [UserDetails, setUserDetails] = useState<{ id: string, displayName: string, email: string }[]>([]);
  const { requestProfileData } = useUserProfile();
  const { t } = useTranslation();

  const { instance, accounts } = useMsal();

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  };

  useEffect(() => {
    fetchToken();
  }, []);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        await requestProfileData();
        const userMail = localStorage.getItem("UserMail");
        const userName = localStorage.getItem("UserName");
        const userId = localStorage.getItem("UserId");
        setUserDetails([{ id: userId || '', displayName: userName || 'Default Nominator', email: userMail || '' }]);
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
  }, [requestProfileData]);

  const fetchNominations = async () => {
    try {
      const token = await fetchToken();
      const UserId = UserDetails[0]?.id;
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/AddPeerComment/peer-award-info?userId=${UserId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const nominationsWithIndex = response.data.responseData.map((responseData: Nomination, index: number) => ({
        ...responseData,
        index: index + 1,
      }));
      setNominations(nominationsWithIndex);
    } catch (error) {
      console.error("Error fetching nominations", error);
    }
  };


  useEffect(() => {
    if (UserDetails.length > 0 && UserDetails[0]?.id) {
      fetchNominations();
    }
  }, [UserDetails]);

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleViewNomination = async (nominationId: string) => {
    try {
      setPeerComment("");
      const token = await fetchToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/AddPeerComment/nomination-details?approvalid=${nominationId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data) {
        setSelectedNomination(response.data.responseData);
      }
      setIsModalVisible(true);
    } catch (error) {
      console.error("Error fetching nomination details", error);
    }
  };

  const handleSubmit = async (nominationId: string) => {
    try {
      if (!nominationId || !peerComment.trim()) {
        console.error("Nomination ID or peer comment is missing");
        return;
      }
      var userId = UserDetails[0]?.id;
      if (!userId) return
      const assigned_by = nominations.find(nomination => nomination.nominationId === nominationId)?.assignedBy || "";

      const payload = {
        nominationId: nominationId,
        assignedTo: userId,
        comments: peerComment,
        updatedBy: userId,
        assignedBy: assigned_by
      }
      const token = await fetchToken();
      await axios.post(`${process.env.REACT_APP_API_URL}/api/AddPeerComment/add-peer-comments`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      fetchNominations();
      setIsModalVisible(false);
    } catch (error) {
      console.error(`Error submitting `, error);
    }
  };

  const columns = [
    {
      title: t('No.'),
      dataIndex: 'index',
      key: 'index',
      width: '5%',

    },
    {
      title: t('reward_details'),
      dataIndex: 'rewardDetails',
      key: 'rewardDetails',
      width: '15%',

      render: (_: any, record: Nomination) => `${record.awardName} - ${record.type}`,
    },
    {
      title: t('nominee'),
      dataIndex: 'nomineeName',
      key: 'nomineeName',
      ellipsis: true,
      width: '15%',
      render: (_: any, record: Nomination) => (
        <Tooltip title={record.nomineeNames?.join(', ') || 'N/A'}>
          {record.nomineeNames?.join(', ') || 'N/A'}
        </Tooltip>
      ),
    },
    {
      title: t('nominator'),
      dataIndex: 'nominatorName',
      key: 'nominatorName',
      width: '15%',

    },
    {
      // title: t('assigned_by'),
      title: 'Assigned By',
      dataIndex: 'assignedByName',
      key: 'assignedByName',
      width: '15%',

    },
    {
      title: t('actions'),
      key: 'actions',
      width: '20%',

      render: (_: any, record: Nomination) => (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button icon={<EyeOutlined />}
            onClick={() => handleViewNomination(record.approvalId)}
            disabled={record.status == 'Submitted'}>
            View
          </Button>
          <Tooltip title={record.status === 'Submitted' ? 'You have already added the comments' : 'Please add your comments'}>
            <Button
              disabled={record.status === 'Submitted'}
              style={{ marginLeft: '8px', backgroundColor: record.status === 'Submitted' ? '#4CAF50' : 'gray', color: 'white', width: '84px' }}
            >{record.status === 'Submitted' ? t('Submitted') : t('Pending')}
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '10px', backgroundColor: 'white' }}>
      <div style={{ backgroundColor: 'white', padding: '0', display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
        <div style={{ flex: '1' }}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/home">
                <HomeOutlined style={{ marginRight: '5px' }} />
                {t('home')}
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{t('reviewer')}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <h2 style={{ margin: '24px 0px 11px 0px', fontSize: '24px' }}>Add Your Feedback To Nomination(s) </h2>

      {/* Inline Media Queries for Responsiveness */}
      <div style={{ overflowX: 'auto' }}>
        <Table
          dataSource={nominations}
          columns={columns}
          rowKey="nominationId"
          style={{ overflowX: 'auto' }}
          className="custom-table"
        />
      </div>
      <Modal
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        centered
        width={1000}
        style={{ padding: '4px' }}
      >
        {selectedNomination && (
          <div
            style={{
              padding: '10px',
              backgroundColor: 'white',
              borderRadius: '8px',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontFamily: 'Roboto',
                fontWeight: 600,
                fontSize: '16px',
                color: 'rgba(0, 0, 0, 0.88)',
              }}
            >
              {t('reward_details')}
            </h3>
            <Card bordered={false} style={{ marginTop: '16px' }}>
              <Row gutter={[16, 16]} style={{ rowGap: '8px' }}>
                <Col span={12}>
                  <strong>{t('rewards')}</strong>
                  <br /> {selectedNomination.awardName}
                </Col>
                <Col span={12}>
                  <strong>{t('nomination_date')}</strong>
                  <br /> {new Date(selectedNomination.nominationDate).toLocaleDateString()}
                </Col>
                <Col span={12}>
                  <strong>{t('nominated_by')}</strong>
                  <br /> {selectedNomination.nominatorName || 'N/A'}
                </Col>
                {selectedNomination.TeamName && (
                  <Col span={12}>
                    <strong>{t('team_name')}</strong>
                    <br /> {selectedNomination.TeamName || 'N/A'}
                  </Col>
                )}
                <Col span={12}>
                  <strong>{t('awarded_to')}</strong>
                  <br /> {selectedNomination.nomineeNames?.join(', ') || 'N/A'}
                </Col>
                <Col span={12}>
                  <strong>{t('value_demonstrated')}</strong>
                  <br />
                  <Tag
                    style={{
                      backgroundColor: '#8629A4',
                      color: 'white',
                      padding: '4px',
                    }}
                  >
                    {selectedNomination.valueName}
                  </Tag>
                </Col>
                <Col span={24}>
                  <strong>{t('contribution_Towards')}</strong>
                  <br />
                  {selectedNomination.contributionTowards
                    ?.split(',')
                    .map((item: string, index: number) => (
                      <Tag
                        key={index}
                        style={{
                          backgroundColor: '#8629A4',
                          color: 'white',
                          padding: '4px',
                          marginRight: '4px',
                        }}
                      >
                        {item.trim()}
                      </Tag>
                    ))}
                </Col>
                <Col span={24}>
                  <strong>{t('reward_testimonial')}</strong>
                  <br />
                  <Input.TextArea
                    value={(selectedNomination.citation)}
                    readOnly
                    style={{
                      width: '100%',
                      height: '100px',
                    }}
                  />
                </Col>
                <Col span={24}>
                  <strong>{t('business_outcome')}</strong>
                  <br />
                  <Input.TextArea
                    value={(selectedNomination.businessOutcome)}
                    readOnly
                    style={{
                      width: '100%',
                      height: '80px',
                    }}
                  />
                </Col>
                {selectedNomination?.managerNames && (
                  <Col span={12}>
                    <strong>{t('approval_level1')}</strong>
                    <br /> {selectedNomination?.managerNames.charAt(0).toUpperCase() + selectedNomination.managerNames.slice(1)}
                  </Col>
                )}
                {selectedNomination?.firstLevelApproverNames && (
                  <Col span={12}>
                    <strong>{t('approval_level2')}</strong>
                    <br /> {selectedNomination?.firstLevelApproverNames.charAt(0).toUpperCase() + selectedNomination.firstLevelApproverNames.slice(1)}
                  </Col>
                )}
                {selectedNomination?.secondLevelApproverNames && (
                  <Col span={12}>
                    <strong>{t('approval_level2')}</strong>
                    <br /> {selectedNomination?.secondLevelApproverNames.charAt(0).toUpperCase() + selectedNomination.secondLevelApproverNames.slice(1)}
                  </Col>
                )}
                {Array.isArray(selectedNomination?.peerComments) && selectedNomination.peerComments.length > 0 && (
                  <Col span={24}>
                    <strong>{t('Peer_Comments')}</strong>
                    <br />
                    {selectedNomination.peerComments.map((peer: PeerComment, index: number) =>
                      peer.peerComment.trim() === "" ? null : (
                        <div
                          key={index}
                          style={{
                            background: '#f5f5f5',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            marginBottom: '12px',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                            maxWidth: '500px'
                          }}
                        >
                          <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#8629A4' }}>
                            {peer.peerName}
                          </div>
                          <div style={{ fontSize: '14px', color: '#333', marginBottom: '6px', whiteSpace: 'pre-line' }}>
                            {peer.peerComment}
                          </div>
                        </div>
                      )
                    )}
                  </Col>
                )}
                <Col span={24}>
                  <strong>{t('enter_comments')}</strong>
                  <br />
                  <Input.TextArea
                    value={peerComment}
                    onChange={e => setPeerComment(e.target.value)}
                    placeholder="Enter your comments here"
                    style={{
                      width: '100%',
                      height: '80px',
                    }}
                  />
                </Col>
                <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
                  <br />
                  <Button
                    style={{
                      backgroundColor: peerComment.trim() === "" ? 'gray' : '#8629A4',
                      color: 'white',
                      width: '100px',
                      height: '40px',
                      fontSize: '13px'
                    }}
                    onClick={() => handleSubmit(selectedNomination.nominationId)}
                    disabled={peerComment.trim() === ""} // Disable if comment is empty
                  >
                    Submit
                  </Button>
                </Col>
              </Row>
            </Card>
          </div>
        )
        }
      </Modal >
    </div >
  );
};

export default PeersCommentsComponent;